import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";

import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import { PostReservationLocation, PostReservationModule } from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import Card from "components/card/Card";
import Input from "components/form/input";
import Toggle from "components/form/toggle/Toggle";
import TimePick from "components/timePick/TimePick";
import { unitLabel } from "./unitLabel";
import "./activitySettings.scss";

interface INewLocation {
  key: string;
  title: string;
}

interface IState {
  title: string;
  enableWebBooking: boolean;
  bookingWindowStart: string;
  bookingWindowEnd: string;
  openTime: string;
  cancellationWindow: string;
  editWindow: string;
  bufferWindow: number;
  maxBookingSize: string;
  requireBookingApproval: boolean;
}

export default function Modules() {
  const history = useHistory();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const [locations, setLocations] = useState<INewLocation[]>([]);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const locationInputRef = useRef<HTMLInputElement | null>(null);
  const [state, setState] = useState<IState>({
    title: "",
    enableWebBooking: false,
    bookingWindowStart: "0",
    bookingWindowEnd: "7",
    openTime: "00:00",
    cancellationWindow: "12",
    editWindow: "12",
    bufferWindow: 0,
    maxBookingSize: "10",
    requireBookingApproval: false,
  });

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleToggleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = e.target;
    setState(prevState => ({ ...prevState, [id]: checked }));
  }

  async function handleSaveModule() {
    const res = await PostReservationModule(
      {
        title: state.title,
        enable_web_booking: state.enableWebBooking,
        days_in_advance_start: Number(state.bookingWindowStart),
        days_in_advance_end: Number(state.bookingWindowEnd),
        open_time: state.openTime,
        web_cancellation_hours: Number(state.cancellationWindow),
        web_edit_hours: Number(state.editWindow),
        buffer_window_hours: Number(state.bufferWindow),
        max_booking_size: Number(state.maxBookingSize),
        require_booking_approval: !!state.requireBookingApproval,
      },
      true,
    );

    if (res?.status !== StatusCode.OK || !res?.data?.id) {
      dispatch(showError(t("Error creating activity type")));
      return;
    }

    const moduleId = res.data.id as number;
    const name = unitLabel(state.title, t("Location"));
    for (const title of locations.map(location => location.title.trim()).filter(Boolean)) {
      const locationRes = await PostReservationLocation(
        { title, reservation_module_id: moduleId },
        false,
      );
      if (locationRes?.status !== StatusCode.OK) {
        dispatch(showError(t("Error saving {{name}}", { name })));
        history.push(`/admin/settings/reservations/module/${moduleId}`);
        return;
      }
    }

    dispatch(showSuccess(t("Successfully created activity type")));
    history.push(`/admin/settings/reservations/module/${moduleId}`);
  }

  function addLocation() {
    const key = `new-${Date.now()}`;
    setLocations(prev => [...prev, { key, title: "" }]);
    setFocusKey(key);
  }

  function removeLocation(key: string) {
    setLocations(prev => prev.filter(location => location.key !== key));
  }

  useEffect(() => {
    if (focusKey) {
      locationInputRef.current?.focus();
    }
  }, [focusKey]);

  const disableNewModule =
    !state.title ||
    !state.bookingWindowStart ||
    !state.bookingWindowEnd ||
    !state.cancellationWindow ||
    !state.editWindow ||
    !state.openTime;

  return (
    <Page
      title={t("New Activity Type")}
      subtitle={t("Create a new activity type")}
      narrow
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Activity Types"),
          url: "/admin/settings/reservations/modules",
        },
      ]}
      primaryAction={{
        content: t("Save"),
        action: () => handleSaveModule(),
        disabled: disableNewModule,
      }}
    >
      <Card>
        <Card.Section>
          <h2 className="activity-settings-heading">{t("General Settings")}</h2>
          <div className="activity-settings-general">
            <div className="activity-settings-title">
              <Input
                label={t("Activity Type")}
                placeholder={t("Activity Type")}
                value={state.title ?? ""}
                onChange={handleInputChange}
                id="title"
              />
            </div>
            <div className="activity-settings-toggles">
              <Toggle
                labelRight={t("Enable Web Booking")}
                id="enableWebBooking"
                checked={state.enableWebBooking}
                onChange={handleToggleChange}
              />
            </div>
          </div>
        </Card.Section>
        <Card.Section>
          <h2 className="activity-settings-heading">{t("Locations")}</h2>
          <div className="activity-locations-form">
            {locations.map(location => (
              <div key={location.key} className="activity-locations-form-row">
                <Input
                  id={`location-${location.key}`}
                  label={t("Location name")}
                  labelHidden
                  value={location.title}
                  placeholder={t("Location name")}
                  inputRef={focusKey === location.key ? locationInputRef : undefined}
                  onChange={event =>
                    setLocations(prev =>
                      prev.map(item => (item.key === location.key ? { ...item, title: event.target.value } : item)),
                    )
                  }
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addLocation();
                    }
                  }}
                />
                <button
                  type="button"
                  className="activity-locations-form-remove"
                  aria-label={t("Remove")}
                  onClick={() => removeLocation(location.key)}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            ))}
            <button type="button" className="activity-locations-form-add" onClick={addLocation}>
              <FontAwesomeIcon icon={faPlus} />
              {t("Add location")}
            </button>
          </div>
        </Card.Section>
        <Card.Section>
          <h2 className="activity-settings-heading">{t("Booking Configuration")}</h2>
          <div className="activity-settings-grid activity-settings-grid--four">
            <Input
              value={state.bookingWindowStart ?? ""}
              label={t("Booking Window Start")}
              id="bookingWindowStart"
              onChange={handleInputChange}
              suffix="days"
              type="number"
              placeholder={0}
            />
            <Input
              value={state.bookingWindowEnd ?? ""}
              label={t("Booking Window End")}
              id="bookingWindowEnd"
              onChange={handleInputChange}
              suffix="days"
              type="number"
              placeholder={0}
            />
            <TimePick
              value={state.openTime}
              onChange={timeString => setState(prevState => ({ ...prevState, openTime: timeString }))}
              label={t("Open Time")}
              size="large"
              status={state.openTime === undefined ? "warning" : undefined}
            />
            <Input
              value={state?.bufferWindow ?? ""}
              label={t("Buffer Window")}
              id="bufferWindow"
              helpText={t("Bookings allowed up to (hours in advance)")}
              onChange={handleInputChange}
              suffix="hours"
              placeholder={t("Buffer Window")}
              type="number"
            />
            <div className="activity-settings-booking-size">
              <Input
                label={t("Maximum booking size")}
                id="maxBookingSize"
                value={state.maxBookingSize ?? ""}
                onChange={handleInputChange}
                type="number"
                min={1}
                helpText={t("Maximum guests allowed in a single reservation booking")}
              />
              <Toggle
                labelRight={t("Require Booking Approval")}
                id="requireBookingApproval"
                checked={!!state.requireBookingApproval}
                onChange={handleToggleChange}
              />
            </div>
          </div>
        </Card.Section>
        <Card.Section>
          <h2 className="activity-settings-heading">{t("Cancellation Policies")}</h2>
          <div className="activity-settings-grid">
            <Input
              value={state.cancellationWindow ?? ""}
              label={t("Cancellation Window")}
              id="cancellationWindow"
              helpText={t("Cancellations allowed up to (hours in advance)")}
              onChange={handleInputChange}
              suffix="hours"
              placeholder={t("Cancellation Window")}
              type="number"
            />
            <Input
              value={state.editWindow ?? ""}
              label={t("Edit Window")}
              id="editWindow"
              helpText={t("Changes allowed up to (hours in advance)")}
              onChange={handleInputChange}
              suffix="hours"
              placeholder={t("Edit Window")}
              type="number"
            />
          </div>
        </Card.Section>
      </Card>
    </Page>
  );
}
